import type { PaginationState, Table } from '@tanstack/react-table'

import LeftChevronIcon from '@/assets/icons/icon-chevron-left.svg'
import RightChevronIcon from '@/assets/icons/icon-chevron-right.svg'

import { Button } from '../button.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select.tsx'

interface IDataTablePaginationProps<TData> {
  table: Table<TData>
  allRowsLength: number
  pagination: PaginationState
}

const paginationOptions = [10, 20, 30, 40, 50]

export const DataTablePagination = <TData,>({
  table,
  allRowsLength,
  pagination,
}: IDataTablePaginationProps<TData>) => {
  const handleSelectChange = (value: string) => {
    table.setPageSize(Number(value))
  }

  const handlePreviousButtonClick = () => {
    table.previousPage()
  }

  const handleNextButtonClick = () => {
    table.nextPage()
  }

  const getFirstItemIndex = () => {
    if (table.getRowModel().rows.length) {
      return pagination.pageSize * pagination.pageIndex + 1
    }

    return 0
  }

  const getLastItemIndex = () => {
    const rowsLength = table.getRowModel().rows.length

    if (rowsLength) {
      return pagination.pageSize * pagination.pageIndex + rowsLength
    }

    return 0
  }

  return (
    <div className="mt-6 flex w-full flex-col items-center justify-between gap-2 sm:flex-row">
      <div className="flex items-center gap-4 text-sm">
        <div className="text-secondary-500 text-slate-900 dark:text-white">
          Rows per page
        </div>
        <Select
          onValueChange={handleSelectChange}
          value={`${pagination.pageSize}`}
        >
          <SelectTrigger className="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {paginationOptions.map((pageSize) => (
                <SelectItem
                  key={pageSize}
                  value={`${pageSize}`}
                  className="hover:bg-background text-center dark:hover:text-white"
                >
                  {pageSize}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <div className="text-sm dark:text-white">
          {getFirstItemIndex()} - {getLastItemIndex()} of {allRowsLength}
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          disabled={!table.getCanPreviousPage()}
          variant="outline"
          onClick={handlePreviousButtonClick}
        >
          <LeftChevronIcon className="fill-secondary-500" />
          Previous
        </Button>
        <Button
          variant="outline"
          onClick={handleNextButtonClick}
          disabled={!table.getCanNextPage()}
        >
          Next
          <RightChevronIcon className="fill-secondary-500" />
        </Button>
      </div>
    </div>
  )
}
